import { FirebaseAppCheck } from '@capacitor-firebase/app-check';
import {
  CustomProvider,
  getToken,
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from 'firebase/app-check';
import type { FirebaseApp } from 'firebase/app';
import { getLocale } from '@/i18n';
import { isNativeAndroid, isNativeApp, isNativeIos } from './platform';

let instance: AppCheck | null = null;
let tokenFailureLogged = false;

/**
 * Initializes App Check before Auth, Firestore, or Storage are created.
 *
 * The packaged apps use native Play Integrity / App Attest tokens. A custom
 * Web SDK provider bridges that native token into the JavaScript Firebase SDK,
 * so direct Firestore and Storage calls can use the same attestation as the
 * custom Cloud Run API. The browser uses reCAPTCHA Enterprise directly.
 */
export function initializeGrundoAppCheck(app: FirebaseApp): AppCheck | null {
  if (import.meta.env.VITE_USE_EMULATORS === '1') return null;

  if (isNativeApp()) {
    const nativeReady = FirebaseAppCheck.initialize({
      isTokenAutoRefreshEnabled: true,
    });
    const provider = new CustomProvider({
      getToken: async () => {
        await nativeReady;
        const result = await FirebaseAppCheck.getToken({ forceRefresh: false });
        const expiresAt = result.expireTimeMillis;
        if (!Number.isFinite(expiresAt) || expiresAt === undefined || expiresAt <= Date.now()) {
          throw new Error('A natív App Check-token lejárata hiányzik vagy érvénytelen.');
        }
        return { token: result.token, expireTimeMillis: expiresAt };
      },
    });
    instance = initializeAppCheck(app, {
      provider,
      isTokenAutoRefreshEnabled: true,
    });
    return instance;
  }

  const siteKey = (import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? '').trim();
  if (!siteKey) return null;

  instance = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
  return instance;
}

/**
 * The client platform, sent with every API call (#80, S2): the server logs it
 * next to a missing App Check token, so `enforce` can be switched on per
 * measured platform instead of by guess. ⚠️ The backend must allow the header
 * in CORS first (`server.ts`) — deploy backend before frontend.
 */
function platformHeader(): Record<string, string> {
  const platform = isNativeIos() ? 'ios' : isNativeAndroid() ? 'android' : 'web';
  return { 'X-Grundo-Platform': platform, ...localeHeader() };
}

/**
 * The UI language for server-made text (#81). A custom header, NOT
 * `Accept-Language`: browsers and WebViews send that one on their own
 * (`en-US` on most devices), so old clients would suddenly get English.
 * Absent means Hungarian — so it is only sent for other languages, and a
 * Hungarian user never depends on the backend's CORS allowing it.
 */
function localeHeader(): Record<string, string> {
  const locale = getLocale();
  return locale === 'hu' ? {} : { 'X-Grundo-Locale': locale };
}

/** Header for the custom Cloud Run backend. Missing setup stays rollout-safe. */
export async function appCheckHeader(): Promise<Record<string, string>> {
  if (!instance) return platformHeader();
  try {
    const result = await getToken(instance, false);
    tokenFailureLogged = false;
    return { ...platformHeader(), 'X-Firebase-AppCheck': result.token };
  } catch (error) {
    if (!tokenFailureLogged) {
      tokenFailureLogged = true;
      // eslint-disable-next-line no-console
      console.warn('[GRUNDO] Az App Check-token nem kérhető le.', error);
    }
    return platformHeader();
  }
}
